import type { Expediente, Group, Standard, Candidate } from "@/data";
import type { AppUser } from "@/data/seed";

export type AlertSeverity = "alta" | "media" | "baja";
export interface ComputedAlert {
  id: string;
  titulo: string;
  detalle: string;
  severidad: AlertSeverity;
  expedienteId?: string;
  estandar?: string;
  evaluadorId?: string;
  tipo: "sin-dictamen" | "sobre-capacidad" | "criterios-bajos" | "certificado-por-vencer";
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return 0;
  return Math.floor((db - da) / 86400000);
}

function parseVigenciaYears(v: string | undefined): number {
  if (!v) return 0;
  const m = v.match(/(\d+)\s*año/i);
  return m ? parseInt(m[1], 10) : 0;
}

export interface AlertContext {
  expedientes: Expediente[];
  groups: Group[];
  standards: Record<string, Standard>;
  candidates: Record<string, Candidate>;
  users: Record<string, AppUser>;
  today: string; // YYYY-MM-DD
}

export function computeAlerts(ctx: AlertContext): ComputedAlert[] {
  const out: ComputedAlert[] = [];
  const { expedientes, groups, standards, today, users } = ctx;

  // 1. Sin dictamen tras X días (desde fecha de evaluación práctica)
  for (const exp of expedientes) {
    if (exp.dictamen !== "pendiente") continue;
    if (!exp.evaluacionPractica.aplicada) continue;
    const f = exp.evaluacionPractica.fecha;
    if (!f || f === "—") continue;
    const d = daysBetween(f, today);
    if (d >= 5) {
      out.push({
        id: `sd-${exp.id}`,
        titulo: "Expediente sin dictamen",
        detalle: `${d} días sin dictaminar desde la evaluación práctica (${f})`,
        severidad: d >= 10 ? "alta" : "media",
        expedienteId: exp.id,
        estandar: exp.estandar,
        tipo: "sin-dictamen",
      });
    }
  }

  // 2. Evaluador sobre capacidad: candidatos por evaluador agregados por grupo
  const loadByEval = new Map<string, number>();
  for (const g of groups) {
    const evals = g.evaluadoresIds && g.evaluadoresIds.length > 0 ? g.evaluadoresIds : [g.evaluadorId];
    if (evals.length === 0) continue;
    const share = g.candidatosIds.length / evals.length;
    for (const evId of evals) {
      loadByEval.set(evId, (loadByEval.get(evId) ?? 0) + share);
    }
  }
  for (const [evId, load] of loadByEval) {
    const u = users[evId];
    const cap = u?.capacidad ?? 12;
    if (load > cap) {
      out.push({
        id: `cap-${evId}`,
        titulo: `Evaluador sobre capacidad`,
        detalle: `${u?.nombre ?? evId} con ${Math.round(load)} candidatos asignados (capacidad ${cap})`,
        severidad: load > cap * 1.25 ? "alta" : "media",
        evaluadorId: evId,
        tipo: "sobre-capacidad",
      });
    }
  }

  // 3. Criterios bajos en práctica (ámbar/rojo)
  for (const exp of expedientes) {
    if (!exp.evaluacionPractica.aplicada || exp.evaluacionPractica.criteriosTotales === 0) continue;
    const pct = (exp.evaluacionPractica.criteriosCompetente / exp.evaluacionPractica.criteriosTotales) * 100;
    if (pct < 80) {
      out.push({
        id: `cb-${exp.id}`,
        titulo: "Criterios prácticos bajo umbral",
        detalle: `${exp.id.toUpperCase()} con ${Math.round(pct)}% de criterios competentes (mínimo 90%)`,
        severidad: pct < 60 ? "alta" : "media",
        expedienteId: exp.id,
        estandar: exp.estandar,
        tipo: "criterios-bajos",
      });
    } else if (pct < 90 && exp.dictamen === "pendiente") {
      out.push({
        id: `cb-${exp.id}`,
        titulo: "Criterios prácticos en ámbar",
        detalle: `${exp.id.toUpperCase()} con ${Math.round(pct)}% — revisión recomendada antes del dictamen`,
        severidad: "baja",
        expedienteId: exp.id,
        estandar: exp.estandar,
        tipo: "criterios-bajos",
      });
    }
  }

  // 4. Certificados próximos a vencer (<= 180 días)
  for (const exp of expedientes) {
    if (!exp.certificado.emitido || !exp.certificado.fechaEmision) continue;
    const years = parseVigenciaYears(standards[exp.estandar]?.vigencia);
    if (years <= 0) continue;
    const issued = new Date(exp.certificado.fechaEmision);
    const expires = new Date(issued.getFullYear() + years, issued.getMonth(), issued.getDate());
    const daysToExpiry = daysBetween(today, expires.toISOString().slice(0, 10));
    if (daysToExpiry <= 180) {
      out.push({
        id: `cv-${exp.id}`,
        titulo: daysToExpiry < 0 ? "Certificado vencido" : "Certificado próximo a vencer",
        detalle: `${exp.certificado.folio ?? exp.id} ${daysToExpiry < 0 ? `vencido hace ${-daysToExpiry} días` : `vence en ${daysToExpiry} días`}`,
        severidad: daysToExpiry < 0 ? "alta" : daysToExpiry <= 60 ? "media" : "baja",
        expedienteId: exp.id,
        estandar: exp.estandar,
        tipo: "certificado-por-vencer",
      });
    }
  }

  const order: Record<AlertSeverity, number> = { alta: 0, media: 1, baja: 2 };
  return out.sort((a, b) => order[a.severidad] - order[b.severidad]);
}
